<template>
  <Authenticated>
    <div class="dashboard">
      
      <section class="card reports-card">
        <div class="reports">
          <div class="reports-top">
            <h1>{{ companySettings.company_name }}</h1>
            <div class="sites-panel">
              <p class="panel-label">{{ active }} Active Site{{ active !== 1 ? "s" : "" }}</p>
              <ul>
                <li v-for="site in siteMap" :key="site.site">
                  <router-link class="site-selector" :to="`/dashboard/${site.site}`">{{ site.site }}</router-link>
                </li>
              </ul>
            </div>
          </div>
          <div class="report-components">
            <ReportsComponent dataType="sales" :site="currentSite" :day="day" :otherDay="otherDay" :period="'today'" class="report-tile" />
            <ReportsComponent dataType="count" :site="currentSite" :day="day" :otherDay="otherDay" :period="'today'" class="report-tile" />
          </div>
        </div>
      </section>

      <section class="card web-store-section">
        <WebStoreCard />
      </section>

      <section class="card">
        <div class="site-box" v-for="site in siteMap" :key="site.site">
          <div class="site-box-header">
            <h2>{{ site.site }}</h2>
            <span class="site-count">{{ site.stations.pos.length + site.stations.kds.length }} stations</span>
          </div>
          <router-link v-for="station in stationLinks(site)" :key="station.label" class="station-unit station-link" :to="station.to">
            <span class="station-label">{{ station.label }} stations</span>
            <span class="station-count">{{ station.count }}</span>
          </router-link>
        </div>
      </section>
    </div>
  </Authenticated>
</template>

<script>
import Authenticated from "../_layouts/Authenticated.vue";
import { mapGetters } from "vuex";
import ReportsComponent from "@/components/Reports/ReportsComponent";
import WebStoreCard from "@/components/Dashboard/WebStoreCard";
import { dayRange } from "@monolith/helpers";

export default {
  name: "DashOption",
  components: { Authenticated, ReportsComponent, WebStoreCard },
  data() {
    return {
      day: dayRange.getToday(),
      otherDay: dayRange.getLastWeekDay(),
      currentSite: null,
    };
  },
  computed: {
    ...mapGetters({
      siteMap: "siteMap",
      companySettings: "companySettings",
      getDefaultSite: "getDefaultSite",
    }),
    active() {
      return this.siteMap.length;
    },
  },
  watch: {
    companySettings: {
      handler() {
        this.currentSite = this.getDefaultSite;
      },
      immediate: true,
    },
  },
  methods: {
    stationLinks(site) {
      return [
        { label: "POS", count: site.stations.pos.length, to: "/pos" },
        { label: "KDS", count: site.stations.kds.length, to: "/kds" },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--tertiary-colour);
  padding: 0.5rem 0.5rem;
  gap: 1rem;
  box-sizing: border-box;
  overflow-y: auto;

}

// Shared card shell
.card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 95%;
  background-color: var(--primary-colour);
  border-radius: 1rem;
  box-shadow: var(--box-shadow);
  padding: 1.25rem;
  gap: 1rem;
}

.reports {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 1rem;
}

.reports-top {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  h1 {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--text-colour);
    text-transform: capitalize;
    letter-spacing: -0.01em;
    text-align: left;
  }
}

.sites-panel {
  min-width: 5rem;
  height: 100%;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;

  .panel-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--action-colour);
    margin: 0 0 0.6rem;
    font-weight: 600;
  }

  ul {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin: 0;
    }

    .site-selector {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 5.5rem;
      min-height: 2.3rem;
      padding: 0.45rem 0.9rem;
      border-radius: 999px;
      background-color: var(--tertiary-colour);
      border: 1px solid transparent;
      color: var(--text-colour);
      text-decoration: none;
      font-size: 0.92rem;
      text-transform: capitalize;
      transition: background-color 0.15s, border-color 0.15s, color 0.15s, transform 0.15s;

      &:hover {
        border-color: var(--action-colour);
        color: var(--action-colour);
        transform: translateY(-1px);
      }

      &.router-link-active,
      &.router-link-exact-active {
        background-color: var(--action-colour);
        border-color: var(--action-colour);
        color: var(--primary-colour);
        box-shadow: 0 0.45rem 1rem rgba(0, 0, 0, 0.12);
      }

      &.router-link-active:hover,
      &.router-link-exact-active:hover {
        color: var(--primary-colour);
      }
    }
  }
}

.report-components {
  flex: 1;
  display: flex;
  gap: 1rem;

  .report-tile {
    flex: 1;
    background-color: var(--tertiary-colour);
    border-radius: 0.75rem;
    padding: 1rem;
    overflow: hidden;
    min-height: 9rem;
  }
}

.site-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0.5rem;
  padding: 1.25rem;
  background-color: var(--secondary-colour);
  box-shadow: var(--box-shadow);
  border-radius: 1rem;
  text-decoration: none;
  color: var(--text-colour);
  transition: box-shadow 0.2s, transform 0.15s;

  &:hover {
    box-shadow: 0 0 1.2rem 0 rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
  }

  .site-box-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    h2 {
      margin: 0;
      font-size: 1.1rem;
      text-transform: capitalize;
      color: var(--text-colour);
    }

    .site-count {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--action-colour);
      opacity: 0.75;
    }
  }

  .station-unit {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--primary-colour);
    border-radius: 0.5rem;
    padding: 0.4rem 0.75rem;
  }

  .station-link {
    text-decoration: none;
    color: var(--text-colour);
    transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 0.8rem 1.25rem rgba(0, 0, 0, 0.08);
      border-color: var(--action-colour);
    }

    .station-label {
      font-size: 0.8rem;
      opacity: 0.65;
    }

    .station-count {
      font-size: 1rem;
      font-weight: 700;
      color: var(--action-colour);
    }
  }

  .go-hint {
    font-size: 0.75rem;
    color: var(--action-colour);
    opacity: 0.55;
    margin-top: auto;
    padding-top: 0.25rem;
  }
}

// ── Portrait ─────────────────────────────────────────────────────────────────
@media only screen and (orientation: portrait) {
  .dashboard {
    padding: 0.5rem 0.5rem 2rem;
    gap: 0.75rem;
  }

  .card {
    flex-direction: column;
    align-items: stretch;
    padding: 0.85rem;
    width: 100%;
    box-sizing: border-box;
    gap: 0.75rem;
  }

  .reports {
    gap: 0.75rem;
  }

  .reports-top {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 0.5rem;

    h1 {
      font-size: 1.3rem;
      text-align: left;
    }

    .sites-panel {
      min-width: unset;
      padding: 0.5rem 0;
      gap: 0.4rem;
    }
  }

  .report-components {
    flex-direction: column;

    .report-tile {
      min-height: 7rem;
    }
  }

  // Station cards stack vertically; each card goes full-width
  .site-box {
    margin: 0;
    padding: 0.85rem;
  }
}
</style>
